import * as z from 'zod';

import { zodDateOrString } from '../../utils';

/**
 * Per-loan record persisted to `InvestorReporting.reportData` for each loan the
 * GNMA Liquidation Event Report (Appendix VI-24) emits. One row per reported
 * liquidation event; dedup / once-per-liquidation state lives on
 * `LoanLerReportedState` — this record is the audit/display artifact of what was
 * actually reported.
 *
 * Fields mirror the LER L-record builder inputs. Per the JSON-persistence
 * convention, leaf fields are optional so the schema can evolve without breaking
 * parsing of existing blobs; only the version and loan identity are required.
 * No PII is persisted (the LER has no S record).
 */
export const GinnieMaeLerReportedRecordV1 = z.object({
  version: z.literal('1'),
  poolId: z.string(),
  ginnieMaeUniqueLoanId: z.string(),

  loanType: z.enum(['FHA', 'FH1', 'FMF', 'RHS', 'RMF', 'PIH', 'VAG', 'VAV']).optional(),
  caseNumber: z.string().optional(),
  issuerLoanId: z.string().optional(),

  firstPaymentDate: zodDateOrString.optional(),
  loanMaturityDate: zodDateOrString.optional(),
  lastInstallmentPaidDate: zodDateOrString.optional(),
  removalDate: zodDateOrString.optional(),

  loanInterestRate: z.number().optional(),
  loanOpb: z.number().optional(),
  loanFic: z.number().optional(),
  inForeclosure: z.boolean().optional(),
  delinquentInterest: z.number().optional(),
  delinquentPrincipal: z.number().optional(),
  prepaidInterest: z.number().optional(),
  prepaidPrincipal: z.number().optional(),
  installInterest: z.number().optional(),
  installPrincipal: z.number().optional(),
  curtailment: z.number().optional(),
  adjustInterest: z.number().optional(),
  netAdjustUpb: z.number().optional(),
  actualLoanUpb: z.number().optional(),
  loanTaxAndInsuranceBalance: z.number().optional(),

  removalReasonCode: z.enum(['1', '2', '3', '4', '5', '6', '7']).optional(),
  liquidationInterestDue: z.number().optional(),
  liquidationPrincipalRemitted: z.number().optional(),
  liquidationPrincipalBalance: z.number().optional(),
});
export type GinnieMaeLerReportedRecordV1 = z.infer<typeof GinnieMaeLerReportedRecordV1>;

export const GinnieMaeLerReportedRecord = GinnieMaeLerReportedRecordV1;
export type GinnieMaeLerReportedRecord = z.infer<typeof GinnieMaeLerReportedRecord>;

// Name consumed by the `InvestorReportingData` discriminated union. Keep this
// alias so the union stays wired when the underlying shape evolves to a
// version-discriminated union later.
export const GinnieMaeLER = GinnieMaeLerReportedRecord;
export type GinnieMaeLER = z.infer<typeof GinnieMaeLER>;
